(() => {
  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0
  });
  const N8N_PRESENCE_PROFILE_WEBHOOK_URL = "https://n8n.srv1650161.hstgr.cloud/webhook/velour-presence-profile";

  function initVelourPresenceProfileV2() {
    const profile = document.querySelector("[data-presence-profile]");
    if (!profile) return;

    const panels = Array.from(profile.querySelectorAll("[data-presence-step]"));
    const progress = profile.querySelector("[data-presence-progress]");
    const resultPanel = profile.querySelector(".velour-presence-result-panel[data-presence-step]");
    const resultStep = Number(resultPanel?.dataset.presenceStep || panels.length - 1);
    const resultTitle = profile.querySelector("[data-presence-result-title]");
    const resultLine = profile.querySelector("[data-presence-result-line]");
    const resultSummary = profile.querySelector("[data-presence-result-summary]");
    const resultWhy = profile.querySelector("[data-presence-result-why]");
    const resultMood = profile.querySelector("[data-presence-result-mood]");
    const fragranceGrid = profile.querySelector("[data-presence-fragrance-products]");
    const ritualGrid = profile.querySelector("[data-presence-ritual-products]");
    const scores = { executive: 0, visionary: 0, minimalist: 0, midnight: 0 };
    const productCache = new Map();
    const quizAnswers = [];
    let genderSignal = "balanced";
    let step = 0;
    let activeResultKey = "executive";

    const archetypes = {
      executive: {
        name: "The Executive",
        line: "Structured. Disciplined. Unforgettable.",
        summary: "You move with quiet command. Your presence is built from clean decisions, sharp rituals and objects placed with intention.",
        why: "This profile suits you because it favours fragrances with structure, projection and a composed trail. The impression is authoritative, warm and controlled.",
        mood: "Polished woods, dark amber, aromatic spice and a tailored evening finish.",
        fragrances: [
          { name: "Asad", handle: "asad-noir-woods" },
          { name: "Club de Nuit Intense", handle: "club-intense-oud" },
          { name: "Fakhar Black", handle: "fakhar-black-silk" }
        ]
      },
      visionary: {
        name: "The Visionary",
        line: "Creative. Magnetic. Distinctive.",
        summary: "You are led by instinct, atmosphere and original taste. Your presence feels warm, expressive and slightly unexpected.",
        why: "This fragrance direction suits you because it blends texture with memorability: sweetness, amber and spice that feel creative without becoming loud.",
        mood: "Amber warmth, soft spice, resinous sweetness and luminous depth.",
        fragrances: [
          { name: "Vintage Radio", handle: "vintage-radio-amber" },
          { name: "Khamrah", handle: "khamrah" },
          { name: "Ana Abiyedh Rouge", handle: "ana-rouge-musk" }
        ]
      },
      minimalist: {
        name: "The Minimalist",
        line: "Calm. Elegant. Intentional.",
        summary: "Your luxury is restraint. You prefer clarity, soft materials, quiet confidence and details that never fight for attention.",
        why: "This profile suits you because it keeps the scent close, elegant and cleanly memorable. It is refined enough for repetition and soft enough for daily ritual.",
        mood: "Creamy musk, pale florals, airy vanilla and smooth modern softness.",
        fragrances: [
          { name: "Noble Blush", handle: "noble-blush" },
          { name: "Bade'e Al Oud Amethyst", handle: "badee-al-oud-sublime-copy" },
          { name: "Ana Rouge Musk", handle: "ana-rouge-musk" }
        ]
      },
      midnight: {
        name: "The Midnight Presence",
        line: "Mysterious. Intense. Unforgettable.",
        summary: "You are drawn to shadow, velvet and lasting impact. Your presence is cinematic, private and difficult to erase from memory.",
        why: "This profile suits you because it leans into darker materials: oud, smoke, spice and dense warmth that stay behind after you leave.",
        mood: "Oud, incense, smoked woods, burgundy warmth and nocturnal spice.",
        fragrances: [
          { name: "Oud for Glory", handle: "badee-al-oud-oud-for-glory" },
          { name: "Bade'e Al Oud", handle: "badee-al-oud-reserve" },
          { name: "Club de Nuit Intense", handle: "club-intense-oud" }
        ]
      }
    };

    const ritualProducts = [
      { name: "Scent Display Tray", handle: "scent-display-tray" },
      { name: "Executive Watch Display", handle: "executive-watch-display" },
      { name: "Velour Display Edit", handle: "executive-watch-display" }
    ];

    const activateStep = (nextStep) => {
      step = Math.max(0, Math.min(nextStep, panels.length - 1));
      panels.forEach((panel) => {
        panel.classList.toggle("is-active", Number(panel.dataset.presenceStep) === step);
      });
      if (progress) progress.style.width = `${Math.min(100, (step / (panels.length - 1)) * 100)}%`;
    };

    const winner = () => Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || "executive";

    const recordAnswer = (control) => {
      const panel = control.closest("[data-presence-step]");
      const answerStep = Number(panel?.dataset.presenceStep || step);
      const question = panel?.querySelector("h2")?.textContent.trim() || "";
      const answer = control.textContent.trim();
      const archetype = control.dataset.archetype || "";
      const existing = quizAnswers.findIndex((item) => item.step === answerStep);
      const answerData = { step: answerStep, question, answer, archetype };
      if (existing >= 0) quizAnswers.splice(existing, 1, answerData);
      else quizAnswers.push(answerData);
    };

    const weightedFragrances = (items) => {
      const weights = {
        masculine: { "asad-noir-woods": 3, "club-intense-oud": 3, "fakhar-black-silk": 2, "badee-al-oud-oud-for-glory": 3, "badee-al-oud-reserve": 2 },
        feminine: { "ana-rouge-musk": 3, "noble-blush": 3, "badee-al-oud-sublime-copy": 3, "vintage-radio-amber": 1, "khamrah": 1 },
        balanced: { "vintage-radio-amber": 2, "khamrah": 2, "ana-rouge-musk": 2, "badee-al-oud-reserve": 1, "club-intense-oud": 1 },
        unspecified: {}
      };
      const selected = weights[genderSignal] || weights.balanced;
      return [...items].sort((a, b) => (selected[b.handle] || 0) - (selected[a.handle] || 0));
    };

    const genderPreferenceLabel = () => ({
      masculine: "Masculine",
      feminine: "Feminine",
      balanced: "Balanced / Fluid",
      unspecified: "Prefer not to say"
    })[genderSignal] || "Balanced / Fluid";

    const buildResultData = () => {
      const key = winner();
      const result = archetypes[key] || archetypes.executive;
      const recommendedFragrances = weightedFragrances(result.fragrances);
      const fragranceNames = recommendedFragrances.map((item) => item.name);
      const displayNames = ritualProducts.map((item) => item.name);
      return {
        key,
        result,
        archetype: result.name,
        genderPreference: genderPreferenceLabel(),
        recommendedFragrances: fragranceNames,
        recommendedDisplays: displayNames,
        recommendedFragranceProducts: recommendedFragrances,
        recommendedDisplayProducts: ritualProducts,
        recommendedProducts: [...fragranceNames, ...displayNames],
        fragranceFamily: result.mood,
        quizAnswers: [...quizAnswers],
        scentMoodAnswers: quizAnswers.filter((item) => item.question || item.answer)
      };
    };

    const normalizeN8nRecommendation = (payload, fallback) => {
      if (!payload || typeof payload !== "object") return null;
      const fragrances = Array.isArray(payload.recommended_fragrances) ? payload.recommended_fragrances : [];
      const displays = Array.isArray(payload.recommended_displays) ? payload.recommended_displays : [];
      if (!fragrances.length) return null;
      const normalizeItems = (items) => items
        .filter((item) => item && item.handle)
        .map((item) => ({
          name: item.title || item.name || item.handle,
          title: item.title || item.name || item.handle,
          handle: item.handle,
          reason: item.reason || ""
        }));
      const recommendedFragranceProducts = normalizeItems(fragrances);
      const recommendedDisplayProducts = normalizeItems(displays);
      if (!recommendedFragranceProducts.length) return null;

      return {
        ...fallback,
        result: {
          ...fallback.result,
          name: payload.profile || fallback.result.name,
          line: payload.tagline || fallback.result.line,
          summary: payload.personality_message || fallback.result.summary,
          why: payload.why_it_suits_you || fallback.result.why,
          mood: payload.scent_mood || fallback.result.mood
        },
        archetype: payload.profile || fallback.archetype,
        recommendedFragrances: recommendedFragranceProducts.map((item) => item.title),
        recommendedDisplays: recommendedDisplayProducts.map((item) => item.title),
        recommendedProducts: [
          ...recommendedFragranceProducts.map((item) => item.title),
          ...recommendedDisplayProducts.map((item) => item.title)
        ],
        recommendedFragranceProducts,
        recommendedDisplayProducts,
        fragranceFamily: payload.scent_mood || fallback.fragranceFamily,
        source: "n8n"
      };
    };

    const requestN8nRecommendation = async (email, resultData) => {
      const trimmedEmail = String(email || "").trim();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 25000);
      try {
        console.log("n8n Presence Profile recommendation requested");
        const response = await fetch(N8N_PRESENCE_PROFILE_WEBHOOK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          signal: controller.signal,
          body: JSON.stringify({
            email: trimmedEmail,
            gender_preference: resultData.genderPreference,
            archetype: resultData.archetype,
            fragrance_family: resultData.fragranceFamily,
            quiz_answers: resultData.quizAnswers,
            scent_mood_answers: resultData.scentMoodAnswers,
            fallback_recommendations: {
              personality_message: resultData.result.summary,
              why_it_suits_you: resultData.result.why,
              fragrance_family: resultData.fragranceFamily,
              recommended_fragrances: resultData.recommendedFragranceProducts.map((item) => ({
                title: item.name,
                handle: item.handle,
                reason: ""
              })),
              recommended_displays: resultData.recommendedDisplayProducts.map((item) => ({
                title: item.name,
                handle: item.handle,
                reason: ""
              }))
            },
            timestamp: new Date().toISOString()
          })
        });
        if (!response.ok) throw new Error(`n8n recommendation failed with ${response.status}`);
        const payload = await response.json();
        const recommendation = normalizeN8nRecommendation(payload, resultData);
        if (!recommendation) throw new Error("n8n returned an invalid recommendation");
        console.log("n8n Presence Profile recommendation received");
        return recommendation;
      } catch (error) {
        console.error("n8n Presence Profile recommendation failed", error);
        return resultData;
      } finally {
        window.clearTimeout(timeout);
      }
    };

    const escapeHtml = (value = "") => String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

    const fetchProduct = async ({ name, handle }) => {
      if (productCache.has(handle)) return productCache.get(handle);
      try {
        const response = await fetch(`/products/${handle}.js`, { headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error("Product unavailable");
        const product = await response.json();
        const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
        const normalized = {
          name,
          available: Boolean(variant?.available),
          title: product.title || name,
          url: product.url || `/products/${handle}`,
          image: product.featured_image || product.images?.[0] || "",
          price: Number(variant?.price || product.price || 0),
          variantId: variant?.id
        };
        productCache.set(handle, normalized);
        return normalized;
      } catch (error) {
        const fallback = { name, available: false, missing: true };
        productCache.set(handle, fallback);
        return fallback;
      }
    };

    const renderProducts = async (grid, items) => {
      if (!grid) return;
      grid.innerHTML = items.map((item) => `
        <article class="velour-presence-product-card is-loading">
          <div class="velour-presence-product-image"></div>
          <div><h3>${escapeHtml(item.name)}</h3><p>Preparing recommendation.</p></div>
        </article>
      `).join("");
      const products = await Promise.all(items.map(fetchProduct));
      grid.innerHTML = products.map((product) => {
        if (product.missing) {
          return `
            <article class="velour-presence-product-card is-missing">
              <div class="velour-presence-product-image">
                ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title || product.name)}" loading="lazy">` : `<span>${escapeHtml(product.name).slice(0, 1)}</span>`}
              </div>
              <div><h3>${escapeHtml(product.title || product.name)}</h3><p>Coming soon to Velour.</p></div>
            </article>
          `;
        }
        return `
          <article class="velour-presence-product-card">
            <a class="velour-presence-product-image" href="${escapeHtml(product.url)}">
              ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : `<span>${escapeHtml(product.title).slice(0, 1)}</span>`}
            </a>
            <div>
              <h3>${escapeHtml(product.title)}</h3>
              <p>${money.format(product.price / 100)}</p>
              <span>${product.available && product.variantId ? "Available" : "Currently unavailable"}</span>
              ${product.available && product.variantId ? `<button type="button" data-quick-add="${product.variantId}">Quick add</button>` : `<button type="button" disabled>Unavailable</button>`}
            </div>
          </article>
        `;
      }).join("");
    };

    const reveal = (resultData = buildResultData()) => {
      activeResultKey = resultData.key;
      const result = resultData.result;
      const recommendedFragrances = resultData.recommendedFragranceProducts;
      if (resultTitle) resultTitle.textContent = result.name;
      if (resultLine) resultLine.textContent = result.line;
      if (resultSummary) resultSummary.textContent = result.summary;
      if (resultWhy) resultWhy.textContent = result.why;
      if (resultMood) resultMood.textContent = result.mood;
      renderProducts(fragranceGrid, recommendedFragrances);
      renderProducts(ritualGrid, resultData.recommendedDisplayProducts);
      activateStep(resultStep);
    };

    const completeProfile = async (email = "") => {
      const fallbackResult = buildResultData();
      const recommendation = await requestN8nRecommendation(email, fallbackResult);
      reveal(recommendation);
    };

    profile.addEventListener("click", (event) => {
      const control = event.target.closest("[data-presence-gender], [data-presence-answer], [data-presence-next], [data-presence-retake]");
      if (!control) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (control.matches("[data-presence-gender]")) {
        genderSignal = control.dataset.presenceGender || "balanced";
        recordAnswer(control);
        control.classList.add("is-selected");
        window.setTimeout(() => activateStep(step + 1), 180);
        return;
      }

      if (control.matches("[data-presence-answer]")) {
        const archetype = control.dataset.archetype;
        if (Object.prototype.hasOwnProperty.call(scores, archetype)) scores[archetype] += 1;
        recordAnswer(control);
        control.classList.add("is-selected");
        window.setTimeout(() => activateStep(step + 1), 180);
        return;
      }

      if (control.matches("[data-presence-next]")) {
        if (step >= resultStep - 1) completeProfile();
        else activateStep(step + 1);
        return;
      }

      Object.keys(scores).forEach((key) => { scores[key] = 0; });
      quizAnswers.splice(0, quizAnswers.length);
      genderSignal = "balanced";
      profile.querySelectorAll(".is-selected").forEach((item) => item.classList.remove("is-selected"));
      activateStep(0);
    }, true);

    profile.querySelector("[data-presence-email-form]")?.addEventListener("submit", (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const email = event.currentTarget.querySelector('input[type="email"]')?.value.trim() || "";
      completeProfile(email);
    }, true);

    activateStep(0);
  }

  document.addEventListener("DOMContentLoaded", initVelourPresenceProfileV2);
})();
